import { VALIDATION_MESSAGES } from './validationMessages';

export interface ValidationError {
  field: string;
  message: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

export function validateEmail(email: string): boolean {
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  return emailRegex.test(email);
}

export function validatePassword(password: string): ValidationResult {
  const errors: ValidationError[] = [];

  if (password.length < 8) {
    errors.push({
      field: "password",
      message: VALIDATION_MESSAGES.PASSWORD_MIN_LENGTH,
    });
  }

  if (!/(?=.*[a-z])/.test(password)) {
    errors.push({
      field: "password",
      message: VALIDATION_MESSAGES.PASSWORD_LOWERCASE,
    });
  }

  if (!/(?=.*[A-Z])/.test(password)) {
    errors.push({
      field: "password",
      message: VALIDATION_MESSAGES.PASSWORD_UPPERCASE,
    });
  }

  if (!/(?=.*\d)/.test(password)) {
    errors.push({
      field: "password",
      message: VALIDATION_MESSAGES.PASSWORD_NUMBER,
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

export function validateLoginForm(data: {
  email: string;
  password: string;
}): ValidationResult {
  const errors: ValidationError[] = [];

  if (!data.email) {
    errors.push({ field: "email", message: VALIDATION_MESSAGES.LOGIN_EMAIL_REQUIRED });
  } else if (!validateEmail(data.email)) {
    errors.push({
      field: "email",
      message: VALIDATION_MESSAGES.EMAIL_INVALID,
    });
  }

  if (!data.password) {
    errors.push({ field: "password", message: VALIDATION_MESSAGES.LOGIN_PASSWORD_REQUIRED });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

export function validateRegisterForm(data: {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  instagramHandle?: string;
}): ValidationResult {
  const errors: ValidationError[] = [];

  if (!data.firstName.trim()) {
    errors.push({ field: "firstName", message: VALIDATION_MESSAGES.FIRST_NAME_REQUIRED });
  } else if (data.firstName.trim().length < 2) {
    errors.push({ field: "firstName", message: VALIDATION_MESSAGES.FIRST_NAME_MIN_LENGTH });
  }

  if (!data.lastName.trim()) {
    errors.push({ field: "lastName", message: VALIDATION_MESSAGES.LAST_NAME_REQUIRED });
  } else if (data.lastName.trim().length < 2) {
    errors.push({ field: "lastName", message: VALIDATION_MESSAGES.LAST_NAME_MIN_LENGTH });
  }

  if (!data.email) {
    errors.push({ field: "email", message: VALIDATION_MESSAGES.EMAIL_REQUIRED });
  } else if (!validateEmail(data.email)) {
    errors.push({
      field: "email",
      message: VALIDATION_MESSAGES.EMAIL_INVALID,
    });
  }

  const passwordValidation = validatePassword(data.password);
  if (!passwordValidation.isValid) {
    errors.push(...passwordValidation.errors);
  }

  if (!data.confirmPassword) {
    errors.push({ field: "confirmPassword", message: VALIDATION_MESSAGES.CONFIRM_PASSWORD_REQUIRED });
  } else if (data.password !== data.confirmPassword) {
    errors.push({
      field: "confirmPassword",
      message: VALIDATION_MESSAGES.PASSWORDS_DO_NOT_MATCH,
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}